import { Package, CreditCard } from 'lucide-react';

interface OrderItem {
  productName: string;
  quantity: number;
  physicianMarkup: number;
  lineTotal: number;
}

interface OrderRow {
  orderId: string;
  orderNumber: string;
  date: string | null;
  patientName: string;
  items: OrderItem[];
  orderMarkupTotal: number;
}

interface MembershipRow {
  ledgerId: string;
  membershipId: string;
  patientName: string;
  tier: string;
  cycleNumber: number;
  cutDollars: number;
  billedAt: string | null;
}

export interface PhysicianBreakdownData {
  orders: OrderRow[];
  membership: MembershipRow[];
  summary: {
    clinicSiteFeeCents?: number;
    netPayout?: number;
    orderRevenue: number;
    membershipRevenue: number;
    revenue: number;
    orderCount: number;
    membershipCount: number;
  };
}

const money = (n: number) => `$${n.toFixed(2)}`;
const shortDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

/**
 * Itemized breakdown of a physician payout: patient-pay order markup and
 * membership clinic-cut (physicians don't earn from the invoice pipeline).
 */
export function PhysicianPayoutBreakdown({ breakdown }: { breakdown: PhysicianBreakdownData }) {
  const { orders, membership, summary } = breakdown;
  const hasNothing = orders.length === 0 && membership.length === 0;

  if (hasNothing) {
    return <div className="text-center py-8 text-gray-500"><p>No earnings details available for this payout.</p></div>;
  }

  return (
    <div className="space-y-6">
      {/* Order markup */}
      {orders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-blue-600" />
            <h4 className="font-medium text-gray-900">Order markup</h4>
            <span className="text-sm text-gray-500">· {summary.orderCount} order{summary.orderCount !== 1 ? 's' : ''}</span>
            <span className="ml-auto font-semibold text-gray-900">{money(summary.orderRevenue)}</span>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Order</th>
                  <th className="text-left font-medium px-3 py-2">Date</th>
                  <th className="text-left font-medium px-3 py-2">Patient</th>
                  <th className="text-left font-medium px-3 py-2">Items</th>
                  <th className="text-right font-medium px-3 py-2">Markup</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map(o => (
                  <tr key={o.orderId} className="align-top">
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{o.orderNumber || o.orderId.slice(-6)}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{shortDate(o.date)}</td>
                    <td className="px-3 py-2 text-gray-700">{o.patientName}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {o.items.map((it, i) => (
                        <div key={i} className="text-xs">
                          {it.productName} <span className="text-gray-400">×{it.quantity}</span>
                          <span className="text-gray-400"> · {money(it.physicianMarkup)}/ea</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">{money(o.orderMarkupTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Membership clinic-cut */}
      {membership.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-emerald-600" />
            <h4 className="font-medium text-gray-900">Membership clinic-cut</h4>
            <span className="text-sm text-gray-500">· {summary.membershipCount} cycle{summary.membershipCount !== 1 ? 's' : ''}</span>
            <span className="ml-auto font-semibold text-gray-900">{money(summary.membershipRevenue)}</span>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Patient</th>
                  <th className="text-left font-medium px-3 py-2">Tier</th>
                  <th className="text-left font-medium px-3 py-2">Cycle</th>
                  <th className="text-left font-medium px-3 py-2">Billed</th>
                  <th className="text-right font-medium px-3 py-2">Cut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {membership.map(m => (
                  <tr key={m.ledgerId}>
                    <td className="px-3 py-2 text-gray-700">{m.patientName}</td>
                    <td className="px-3 py-2 text-gray-600 capitalize">{m.tier || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">#{m.cycleNumber}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{shortDate(m.billedAt)}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">{money(m.cutDollars)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grand total */}
      {!!summary.clinicSiteFeeCents && <div className="flex justify-between px-3 py-2 text-sm"><span>Clinic domain subscription fees</span><span>−{money(summary.clinicSiteFeeCents / 100)}</span></div>}
      <div className="flex items-center justify-between px-3 py-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total payout earnings</span>
        <span className="text-lg font-bold text-gray-900">{money(summary.netPayout ?? summary.revenue)}</span>
      </div>
    </div>
  );
}
